import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import Konva from 'konva';
import { Stage, Layer, Group, Rect, Text, Path } from 'react-konva';
import { useImageLoader } from '../../lib/hooks.js';

import EditableArea from '../canvas/EditableArea.js';
import { getAbsoluteCoordsAsymmetrical } from '../canvas/utils.js';
import DefaultSportsShirt from '../svg/DefaultSportsShirt.js';
import InstructionsHand from '../svg/InstructionsHand.js';

import { getSectionLimits, getPlayerDorsal } from '../../containers/TeamClient.js';
import { getPromotionMainColor, isNotEmpty, isColorBright } from '../../lib/ep.js';
import EasypromosLogo from '../svg/EasypromosLogo.js';

const standard_board_size_w = 400;		// px
const standard_board_size_h = 600;		// px
const _candidate_w = 60;				// px
const _candidate_h = 72;				// px
const _candidate_h_sq = _candidate_w;	// px
const pad = 10;							// px
const default_player_style = 'none';

// px (canvas on desktop full width. This value is random, is only to have a common value to be compared between all the sizes):
const max_canvas_size_w = 500;

const guides_color = "#FF0000";

const EasypromosWaterMark = (props) => {
	const scale = props?.scale ?? 1;
	const canvas_w = props?.canvas_w ?? standard_board_size_w;
	const canvas_h = props?.canvas_h ?? standard_board_size_h;

	return (
		<Group x={-4 * scale} y={scale * (canvas_h - 20)}>
			<Group scaleX={scale} scaleY={scale}>
				<Rect
					fill='white'
					width={85}
					height={25}
					stroke='rgba(0, 0, 0, 0.5)'
					strokeWidth={0.8}
					cornerRadius={[0, 5, 0, 0]}
				/>

				<Group
					scaleX={0.2}
					scaleY={0.2}
					x={10}
					y={6}
				>
					<EasypromosLogo
						in_konva={true}
						fill='rgba(35, 48, 190, 1)'
					/>
				</Group>
			</Group>
		</Group>
	)
};

const InstructionMessage = (props) => {
	const player_props = props?.player_props ?? {};

	const player_ref = player_props.player_refs.current[player_props.player_ref];

	useEffect(() => {
		if(player_ref != undefined) {
			animateHand();
		}
	}, [ player_ref ]);

	if(player_ref == undefined) return null;

	const hand_initial_state = {
		x:player_props.x + player_props.stage_w,
		y:player_props.y - 20,
		rotation:-10,
	};

	const player_initial_state = {
		x:player_ref.x(),
		y:player_ref.y(),
	};

	const animateHand = () => {
		const has_moved = player_ref.attrs?.has_moved ?? false;
		if(has_moved) return;

		props.refs.current['instructions'].setAttrs(hand_initial_state);

		props.refs.current['instructions'].to({
			x:-5,
			y:player_props.height / 2 + 10,
			rotation:0,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					const has_moved = player_ref.attrs?.has_moved ?? false;

					if(!has_moved) {
						player_ref.to({
							x:player_initial_state.x + 20,
							y:player_initial_state.y + 20,
							duration:0.5,
							easing:Konva.Easings.StrongEaseInOut,
							onFinish:() => {
								player_ref.to({
									...player_initial_state,
									duration:0.5,
									easing:Konva.Easings.StrongEaseInOut,
									onFinish:() => {
										props.refs.current['instructions'].to({
											...hand_initial_state,
											duration:1,
											easing:Konva.Easings.StrongEaseInOut,
											onFinish:() => {
												setTimeout(() => {
													animateHand();
												}, 2000);
											},
										});
									},
								});
							},
						});
					} else {
						player_ref.to({
							...player_initial_state,
							duration:0.5,
							easing:Konva.Easings.StrongEaseInOut,
						});
					}
				}, 300);
			},
		})
	};

	return (
		<Group
			ref={(node) => { props.refs.current['instructions'] = node; }}
		>
			<InstructionsHand />
		</Group>
	)
}

const getPlayerSize = (player_style) => {
	const w = _candidate_w;
	let h = _candidate_h;
	let margin = 5;
	let y = 0;
	let displacement = 20;

	switch(player_style) {
		case 'circle':
		case 'rect':
			h = _candidate_h_sq;
			break;
	}

	return { w, h, margin, y, displacement };
};

const getPlayerYDisplacement = (section_h, candidate_h, player_scale, displacement, canvas_scale = 1) => {
	return ((section_h - candidate_h * player_scale) / 2 - displacement * player_scale);
};

const PlayerInField = ({ group_props, ...props }) => {
	const [ has_instructions, setHasInstructions ] = useState(props?.has_instructions ?? false);

	const team_hide_member_names = props?.team_hide_member_names ?? false;
	const member_scale = props?.member_scale ?? 1;
	const images = props?.images ?? [];
	const player_style = props?.player_style ?? default_player_style;
	const main_promo_color = props?.main_promo_color ?? '#FF0000';
	const section = props?.section ?? {};
	const member = props?.member ?? {};
	const text_props = props?.text_props ?? {};

	let team_sport_type = props?.team_sport_type ?? 'football';
	switch(player_style) {
		case 'circle':
		case 'rect':
			team_sport_type = player_style;
			break;
	}

	const player_size = getPlayerSize(player_style);
	const candidate_w = player_size.w;
	let candidate_h = player_size.h;

	const member_size_w = props?.member_size_w ?? candidate_w;

	const has_candidate_image = (member.image != undefined && member.image != "") ? true : false;
	const cand_img = (has_candidate_image && images[member.id] != undefined) ? images[member.id] : false;
	const cand_img_scale = (cand_img !== false && cand_img.img != undefined) ? Math.min(candidate_w / cand_img.w, candidate_h / cand_img.h) : 1;

	const is_main_color_light = isColorBright(main_promo_color);
	
	useEffect(() => {
		const _has_instructions = props?.has_instructions ?? false;

		if(has_instructions === true && _has_instructions === false) {
			setHasInstructions(false);
		}
	}, [ props.has_instructions ]);
	
	return (
		<Group>
			<Group name='single_player' {...group_props}>
				{(player_style == 'none' && has_candidate_image) ? (
					<Rect
						width={candidate_w}
						height={candidate_h}
						fillPatternImage={cand_img.img}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={cand_img_scale}
						fillPatternScaleY={cand_img_scale}
						x={(member_size_w - candidate_w) / 2}
					/>
				) : (
					<DefaultSportsShirt
						type={team_sport_type}
						dorsal={getPlayerDorsal(member)}
						is_konva={true}
						color={section?.color ?? main_promo_color}
						text_color={section?.text_color ?? (is_main_color_light ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)')}
						decoration_img={(cand_img !== false) ? cand_img : images[`section_${section.id}`]}
						//in_scale={member_scale}
						paint_dorsal={(cand_img !== false) ? false : true}
						konvaProps={{
							x:(member_size_w - candidate_w) / 2,
							y:0,
						}}
					/>

				)}

				{(!team_hide_member_names) && (
					<>
						<Text
							text={member.name}
							stroke='white'
							strokeWidth={2.5 / member_scale}
							lineCap='round'
							lineJoin='round'
							width={member_size_w}
							{...text_props}
						/>

						<Text
							text={member.name}
							width={member_size_w}
							{...text_props}
						/>
					</>
				)}
			</Group>

			{(has_instructions) && (
				<InstructionMessage
					refs={props.refs}
					player_props={props.instructions_props}
				/>
			)}
		</Group>
	)
}

const TeamCanvas = forwardRef((props, ref) => {
	let stage = props.stage;

	const [ size_w, setSizeW ] = useState(props?.canvas_size_w ?? standard_board_size_w);
	const [ size_h, setSizeH ] = useState(props?.canvas_size_h ?? standard_board_size_h);

	const is_builder = props?.is_builder ?? false;
	const is_editing = (is_builder && props.is_editing != undefined) ? props.is_editing : false;
	const is_playable = props?.is_playable ?? true;
	const promotion = props?.promotion ?? false;
	const team_sport_type = stage?.team_sport_type ?? 'football';

	const promo_type = promotion?.promo_type ?? '1';
	const display_watermark = (parseInt(promo_type) < 2) ? true : false;

	const joined_sections = [];
	stage.teams.map((team, t) => {
		const new_sections = (t === ((team_sport_type == 'basketball') ? 1 : 0)) ? [...team.sections] : [...team.sections].reverse();
		joined_sections.push(...new_sections);
	});

	const player_style = stage?.team_player_style ?? default_player_style;
	const player_size = getPlayerSize(player_style);
	const candidate_w = player_size.w;
	const candidate_margin = player_size.margin;
	let candidate_h = player_size.h;
	const candidate_displacement = player_size.displacement;

	// number of players to be displayed in the canvas
	// per section in Design tab at builder
	// (it will only affect if is_editing = true):
	let max_per_section = 3;
	switch(team_sport_type) {
		case 'basketball':
			max_per_section = [
				1, 2, 2,
				2, 2, 1,
			];
			break;

		case 'football':
			max_per_section = [
				1, 4, 3, 3,
				3, 3, 4, 1,
			];
			break;

		default:
			max_per_section = [
				2, 2, 2, 2,
				2, 2, 2, 2,
			];
			break;
	}
	const teams = stage?.teams ?? [];
	let max_players_in_field = 0;
	teams.map((team) => {
		max_players_in_field += parseInt(team?.num_players ?? '1');
	});

	const main_promo_color = getPromotionMainColor(promotion);

	const team_enable_drag_members = (is_playable) ? (stage?.team_enable_drag_members ?? true) : false;
	const team_hide_member_names = stage?.team_hide_member_names ?? false;

	const [ images, setImages ] = useState({});
	const [ is_game_finished, setIsGameFinished ] = useState(false);

	const scale = useRef(1);
	const refs = useRef([]);
	const userPath = useRef(0);
	const section_grid = useRef({});
	const has_moved_a_player = useRef(false);

	const member_scale = parseFloat(stage?.team_player_scale ?? '1');

	const default_cache_props = { pixelRatio:2 * member_scale, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const candidate_h_full = (!team_hide_member_names) ? (candidate_h + 18 / member_scale) : candidate_h;	// image + name

	let group_pos = getAbsoluteCoordsAsymmetrical(stage.reveal_group_area, size_w, size_h);
	group_pos.width = group_pos.w;
	group_pos.height = group_pos.h;

	const canvas_scale = size_w / standard_board_size_w;
	const canvas_real_scale = (canvas_scale * standard_board_size_w) / max_canvas_size_w;

	const getTeamMargin = () => {
		const default_margin = 10;

		return {
			top:canvas_scale * parseInt(stage?.team_margin?.top ?? default_margin),
			bottom:canvas_scale * Math.max(parseInt(stage?.team_margin?.bottom ?? default_margin), ((display_watermark) ? 30 : 0)),
			left:canvas_scale * parseInt(stage?.team_margin?.left ?? default_margin),
			right:canvas_scale * parseInt(stage?.team_margin?.right ?? default_margin),
		}
	};

	const margins = getTeamMargin();
	const active_size_w = size_w - margins.left - margins.right;
	const active_size_h = size_h - margins.top - margins.bottom;

	useImperativeHandle(ref, () => ({
		to_image() {
			if(refs.current['main_stage'] != undefined) {
				if(section_grid.current['instructions'] != undefined) {
					section_grid.current['instructions'].setAttrs({ opacity:0 });
				}

				let dataURL = refs.current['main_stage'].toDataURL({
					mimeType:'image/jpeg',
					quality:0.8,
					pixelRatio:2,
				});

				return dataURL;
			}
		}
	}));

	const reset = (forced) => {
		if(userPath.current == 0 && !forced) return;
		userPath.current = 0;
		setIsGameFinished(true);
		setTimeout(() => setIsGameFinished(false), 0);
	};

	const generateCache = () => {
		if(!is_builder) {
			joined_sections.map((section, s) => {
				let section_players = sections[section.id]?.members ?? [];

				section_players.map((member, m) => {
					section_grid.current[`member_${s}_${m}`].findOne('.single_player').cache(default_cache_props);
				});
			});
		}
	};

	useEffect(() => {
		if(props.canvas_size_w != undefined || props.canvas_size_h != undefined) {
			/*const scale_w = props.canvas_size_w / standard_board_size_w;
			const scale_h = props.canvas_size_h / standard_board_size_h;
			scale.current = Math.min(scale_w, scale_h);*/

			//setSizeW(props.canvas_size_w);
			//setSizeH(props.canvas_size_h);
		}
	}, [ props.canvas_size_w, props.canvas_size_h ]);

	useEffect(() => {
		if(is_builder) {
			reset(true);
		}
	}, [ props.prizeType, stage ]);

	useEffect(() => {
		generateCache();
	}, []);

	const images_to_load = useMemo(() => {
		let bg_image = (isNotEmpty(stage, 'bg_image')) ? (stage?.full_bg_image ?? false) : false;
		if(bg_image === false) bg_image = `${window.cdn_url}img/teams/${team_sport_type}_field.jpg`;

		const images_to_load = [{ name:'background', url:bg_image }];

		stage.teams.map((team) => {
			team.sections.map((section) => {
				if(section.decoration_image == undefined || section.decoration_image == '') return;
		
				images_to_load.push({
					name:`section_${section.id}`,
					url:section.full_decoration_image,
				});
			});

			team.candidates.map((candidate) => {
				if(candidate.image == undefined || candidate.image == '') return;
		
				images_to_load.push({
					name:candidate.id,
					url:candidate.full_image,
				});
			});
		});

		return images_to_load;
	}, [ stage.teams, stage.full_bg_image ]);

	useImageLoader(images_to_load, setImages);
	
	useEffect(() => {
		generateCache();
		refs.current.paint_layer_ref.draw();
	}, [ images ]);

	const moveSection = (area, section) => {
		const abs = getAbsoluteCoordsAsymmetrical(area, active_size_w, active_size_h);
		abs.x = abs.x + margins.left;
		abs.y = abs.y + margins.top;
		section_grid.current[section.id].setAttrs(abs);

		const players_in_section = section_grid.current[section.id].find('.single_player_container');
		const total_players_in_section = Object.values(players_in_section).length;
		let count = 0;

		section_grid.current[section.id].getChildren((player) => {
			if(player.getAttr('name') == 'section_label') return;

			const member_size_w = abs.w / (total_players_in_section - 1);
			const member_size_h = abs.h;

			player.setAttrs({
				x:(count * member_size_w) + (member_size_w / 2),
				width:candidate_w * canvas_scale * member_scale,
				height:candidate_h_full * canvas_scale * member_scale,
			});
			count++;
		});

		refs.current.paint_layer_ref.draw();
	};

	const highlightSection = (id, show) => {
		if(!is_editing || section_grid.current[`section_label_${id}`] == undefined) return;

		section_grid.current[`section_label_${id}`].opacity((show) ? 1 : 0);
		refs.current['paint_layer_ref'].draw();
	};

	const getSections = () => {
		const sections = {};
		
		if(Object.keys(props.user).length > 0) {
			Object.keys(props.user).map((player_id) => {
				const player = props.user[player_id];
				if(sections[player.section_id] == undefined) {
					sections[player.section_id] = {
						members:[],
					}
				}
				sections[player.section_id].members.push(stage.teams.find(t=>(t.id==player.team_id)).candidates.find((c) => c.id == player.id));
			});

			return sections;
		}

		stage.teams.map((team) => {
			team.sections.map((section) => {
				sections[section.id] = {
					name:section.name,
					members:team.candidates.filter((c) => ((section?.candidates ?? []).includes(c.id))).slice(0, getSectionLimits(section, true).max),
				};
			});
		});

		return sections;
	};

	const sections = getSections();

	if(is_game_finished) return null;

	const limitDrag = (pos, id, section_h) => {
		const obj = section_grid.current[id].attrs;

		const candidate_cx = obj.width / 2;	// the candidate offsetX is centered in its width
		const candidate_cy = obj.height;	// the candidate offsetY is in its top

		const y_displacement = getPlayerYDisplacement(section_h, candidate_h_full, member_scale, candidate_displacement);

		return {
			x:Math.min(Math.max(margins.left + candidate_cx, pos.x), size_w - (margins.right + candidate_cx)),
			y:Math.min(Math.max(margins.top - y_displacement, pos.y), size_h - (margins.bottom +
				y_displacement + candidate_cy)),
		};
	};

	const movePlayerToTop = (id) => {
		section_grid.current[id].moveToTop();
		if(has_moved_a_player.current === false) {
			has_moved_a_player.current = true;
			fadeInstructions();
		}
	};

	let top_position_section_index = 0;
	let actual_top_position_y = 1000;
	joined_sections.map((section, s) => {
		const section_y = section?.position?.y ?? ((s == 0) ? 0 : 1);

		if(section_y < actual_top_position_y) {
			top_position_section_index = s;
			actual_top_position_y = section_y;
		}
	});

	const fadeInstructions = () => {
		if(!is_playable) return;

		section_grid.current['instructions'].to({
			opacity:0,
			duration:0.4,
		});

		section_grid.current[`member_${top_position_section_index}_0`].setAttrs({
			has_moved:true,
		});
	};

	const bg_scale = (images.background != undefined) ? Math.max(size_w / images.background.w, size_h / images.background.h) : 1;
	const x1 = margins.left;
	const x2 = size_w - margins.right;
	const y1 = margins.top;
	const y2 = size_h - margins.bottom;

	const text_props = {
		x:0,
		y:candidate_h + candidate_margin * canvas_real_scale,
		align:'center',
		fontSize:15 * canvas_real_scale / member_scale,
		fontStyle:'bold',
	};

	const getDefaultSectionPositions = (section, s) => {
		if(section.position != undefined) return section.position;

		let total_sections_in_field = 0;
		stage.teams.map((team) => {
			total_sections_in_field += team.sections.length;
		});

		const section_h = 1 / total_sections_in_field;

		return {
			w:1,
			h:section_h,
			x:0,
			y:s * section_h,
		};
	};

	let actual_players_in_field = 0;

	const player_props = {
		member_scale,
		images,
		candidate_h_full,
		main_promo_color,
		team_hide_member_names,
		team_sport_type,
		text_props,
	};

	if(is_editing) {
		/* IMPORTANT: THE PUBLIC CANVAS IS AFTER THIS RENDER */
		return (
			<Stage
				ref={(node) => { refs.current['main_stage'] = node; }}
				className="canvas_cont"
				width={size_w}
				height={size_h}
			>
				<Layer ref={(node) => { refs.current['paint_layer_ref'] = node; }}>
					{(images.background != undefined) && (
						<Rect
							width={size_w}
							height={size_h}
							fillPatternImage={images.background.img}
							fillPatternRepeat='no-repeat'
							fillPatternScaleX={bg_scale}
							fillPatternScaleY={bg_scale}
						/>
					)}

					<Group>
						<Path
							data={`M${-pad}${-pad}v${size_h + 2 * pad}h${size_w + 2 * pad}V${-pad}H${-pad}z M${x2},${y2}H${x1}V${y1}h${x2 - x1}V${y2}z`}
							fill={guides_color}
							opacity={0.3}
						/>

						<Rect
							width={active_size_w}
							height={active_size_h}
							x={x1}
							y={y1}
							fill='transparent'
							stroke={guides_color}
							strokeWidth={0.5}
						/>
					</Group>

					{joined_sections.map((section, s) => {
						const section_position = getDefaultSectionPositions(section, s);

						const areas_group_pos = getAbsoluteCoordsAsymmetrical(section_position, active_size_w, active_size_h);
						let section_players = sections[section.id]?.members ?? [];
						let _max_per_section = (typeof max_per_section == 'number') ? max_per_section : max_per_section[s];
						if((actual_players_in_field + _max_per_section) > max_players_in_field) {
							_max_per_section = max_players_in_field - actual_players_in_field;
						}
						section_players = section_players.slice(0, _max_per_section);
						
						const total_players_in_section = section_players.length;

						if(total_players_in_section === 0) return null;
						actual_players_in_field += total_players_in_section;

						const member_size_w = areas_group_pos.w / total_players_in_section;
						const member_size_h = areas_group_pos.h;

						const group_props = {
							scaleX:member_scale * canvas_real_scale,
							scaleY:member_scale * canvas_real_scale,
							offsetX:member_size_w / 2,
							offsetY:candidate_h_full / 2,
							y:member_size_h / 2,
						};

						return (
							<Group
								key={`section_${s}`}
								ref={(node) => { section_grid.current[section.id] = node; }}
								width={areas_group_pos.w}
								height={member_size_h}
								x={areas_group_pos.x + margins.left}
								y={areas_group_pos.y + margins.top}
							>
								{section_players.map((member, m) => {
									const member_id = `member_${s}_${m}`;

									let has_instructions = false;
									if(s == top_position_section_index && m == 0) has_instructions = true;	// only for the first player in the field
									if(!team_enable_drag_members) has_instructions = false;
									if(has_moved_a_player.current !== false) has_instructions = false;
									if(is_builder) has_instructions = false;

									const candidate_props = {
										x:(m * member_size_w) + (member_size_w / 2),
										width:candidate_w * canvas_scale * member_scale,
										height:candidate_h_full * canvas_scale * member_scale,
									};

									return (
										<Group
											key={member_id}
											ref={(node) => { section_grid.current[member_id] = node; }}
											name='single_player_container'
											{...candidate_props}
										>
											<PlayerInField
												{...player_props}
												group_props={group_props}
												member_size_w={member_size_w}
												member={member}
												section={section}
												player_style={player_style}
												has_instructions={has_instructions}
												refs={section_grid}
												instructions_props={{
													...candidate_props,
													stage_w:size_w,
													player_ref:member_id,
													player_refs:section_grid,
												}}
											/>
										</Group>
									)
								})}

								<Group
									ref={(node) => { section_grid.current[`section_label_${section.id}`] = node; }}
									x={5}
									y={5}
									name='section_label'
									opacity={0}
								>
									<Rect
										width={size_w * 0.3}
										height={15 * canvas_scale}
										fill='red'
										cornerRadius={4}
									/>

									<Text
										text={section.name.toUpperCase()}
										{...text_props}
										width={size_w * 0.3}
										x={0}
										y={3.5 * canvas_scale}
										fontSize={9 * canvas_scale}
										fontStyle='bold'
										fill='white'
									/>
								</Group>
							</Group>
						)
					})}

					{joined_sections.map((section, s) => {
						return (
							<Group
								key={`editable_section_${s}`}
								onMouseEnter={() => highlightSection(section.id, true)}
								onMouseLeave={() => highlightSection(section.id, false)}
							>
								<EditableArea
									areaMove={area => moveSection(area, section)}
									area={getDefaultSectionPositions(section, s)}
									areaChanged={(area) => props.areaChanged(section.id, area)}
									guides_color={guides_color}
									is_editing={true}
									x={margins.left}
									y={margins.top}
									size_w={size_w - margins.right - margins.left}
									size_h={size_h - margins.bottom - margins.top}
								/>
							</Group>
						)
					})}

					{(display_watermark) && (
						<EasypromosWaterMark
							scale={canvas_scale}
						/>
					)}
				</Layer>
			</Stage>
		)
	}

	return (
		<Stage
			ref={(node) => { refs.current['main_stage'] = node; }}
			className="canvas_cont"
			width={size_w}
			height={size_h}
		>
			<Layer ref={(node) => { refs.current['paint_layer_ref'] = node; }}>
				{(images.background != undefined) && (
					<Rect
						width={size_w}
						height={size_h}
						fillPatternImage={images.background.img}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={bg_scale}
						fillPatternScaleY={bg_scale}
					/>
				)}

				{joined_sections.map((section, s) => {
					const section_position = getDefaultSectionPositions(section, s);

					const areas_group_pos = getAbsoluteCoordsAsymmetrical(section_position, active_size_w, active_size_h);
					let section_players = sections[section.id]?.members ?? [];

					const total_players_in_section = section_players.length;
					
					if(total_players_in_section === 0) return null;
					actual_players_in_field += total_players_in_section;

					const member_size_w = areas_group_pos.w / total_players_in_section;
					const member_size_h = areas_group_pos.h;

					const section_x = areas_group_pos.x + margins.left;
					const section_y = areas_group_pos.y + margins.top;

					const group_props = {
						scaleX:member_scale * canvas_real_scale,
						scaleY:member_scale * canvas_real_scale,
						offsetX:member_size_w / 2,
						offsetY:candidate_h_full / 2,
						y:member_size_h / 2,
					};

					return (
						section_players.map((member, m) => {
							const member_id = `member_${s}_${m}`;

							let has_instructions = false;
							if(s == top_position_section_index && m == 0) has_instructions = true;	// only for the first player in the field
							if(!team_enable_drag_members) has_instructions = false;
							if(has_moved_a_player.current !== false) has_instructions = false;
							if(is_builder) has_instructions = false;

							const candidate_props = {
								x:(m * member_size_w) + (member_size_w / 2) + section_x,
								y:section_y,
								width:candidate_w * canvas_scale * member_scale,
								height:candidate_h_full * canvas_scale * member_scale,
							};

							return (
								<Group
									key={member_id}
									ref={(node) => { section_grid.current[member_id] = node; }}
									draggable={team_enable_drag_members}
									dragBoundFunc={(team_enable_drag_members) ? (pos) => limitDrag(pos, member_id, member_size_h) : null}
									onDragStart={(team_enable_drag_members) ? () => movePlayerToTop(member_id) : null}
									{...candidate_props}
								>
									<PlayerInField
										{...player_props}
										group_props={group_props}
										member_size_w={member_size_w}
										member={member}
										section={section}
										has_instructions={has_instructions}
										player_style={player_style}
										refs={section_grid}
										instructions_props={{
											...candidate_props,
											stage_w:size_w,
											player_ref:member_id,
											player_refs:section_grid,
										}}
									/>
								</Group>
							)
						})
					)
				})}

				{(display_watermark) && (
					<EasypromosWaterMark
						scale={canvas_scale}
					/>
				)}
			</Layer>
		</Stage>
	)
});

export default TeamCanvas;